
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, type CreatorProfile, type ProfileChangeRequest } from '../../lib/supabase';

const BANK_LIST = [
  '台灣銀行', '土地銀行', '合作金庫', '第一銀行', '華南銀行',
  '彰化銀行', '兆豐銀行', '台灣企銀', '台北富邦', '國泰世華',
  '高雄銀行', '台灣中小企銀', '渣打銀行', '台中銀行', '京城銀行',
  '瑞興銀行', '華泰銀行', '新光銀行', '陽信銀行', '板信銀行',
  '三信銀行', '聯邦銀行', '遠東銀行', '元大銀行', '永豐銀行',
  '玉山銀行', '凱基銀行', '星展銀行', '台新銀行', '日盛銀行',
  '安泰銀行', '中國信託', '上海銀行', '花旗銀行', '匯豐銀行',
  '中華郵政', 'LINE Bank', '樂天銀行', '將來銀行',
];

const FIELD_LABELS: Record<string, string> = {
  phone: '手機號碼',
  email: '電子郵件',
  bank_name: '銀行名稱',
  bank_branch: '分行名稱',
  bank_account_name: '戶名',
  bank_account_number: '銀行帳號',
};

export default function EditProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [lockedFields, setLockedFields] = useState({
    phone: false,
    bank: false,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    line_id: '',
    social_links: '',
    game_type: '',
    bank_name: '',
    bank_branch: '',
    bank_account_name: '',
    bank_account_number: '',
  });

  // 變更請求
  const [pendingRequests, setPendingRequests] = useState<ProfileChangeRequest[]>([]);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<{ type: 'confirm' | 'reject'; requests: ProfileChangeRequest[] } | null>(null);
  const [actionToast, setActionToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }
    loadProfile();
    loadPendingRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (actionToast) {
      const timer = setTimeout(() => setActionToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionToast]);

  const loadProfile = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('creator_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading profile:', error);
        return;
      }

      if (data) {
        setFormData({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          line_id: data.line_id || '',
          social_links: data.social_links || '',
          game_type: data.game_type || '',
          bank_name: data.bank_name || '',
          bank_branch: data.bank_branch || '',
          bank_account_name: data.bank_account_name || '',
          bank_account_number: data.bank_account_number || '',
        });

        setLockedFields({
          phone: !!data.phone,
          bank: !!(data.bank_name && data.bank_account_number),
        });
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('profile_change_requests')
        .select('*')
        .eq('creator_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (data) setPendingRequests(data);
    } catch (err) {
      console.error('Error loading requests:', err);
    }
  };

  // 將同一批次（同 change_type + 同時間）的請求分組
  const groupedRequests = () => {
    const groups: { key: string; type: string; requests: ProfileChangeRequest[]; adminNote: string | null; createdAt: string }[] = [];
    const seen = new Set<string>();

    for (const req of pendingRequests) {
      const groupKey = `${req.change_type}_${req.created_at}`;
      if (seen.has(groupKey)) continue;

      const sameGroup = pendingRequests.filter(
        (r) => r.change_type === req.change_type && r.created_at === req.created_at
      );
      seen.add(groupKey);
      for (const r of sameGroup) {
        seen.add(`${r.change_type}_${r.created_at}`);
      }

      groups.push({
        key: groupKey,
        type: req.change_type,
        requests: sameGroup,
        adminNote: req.admin_note,
        createdAt: req.created_at,
      });
    }
    return groups;
  };

  const handleConfirmGroup = (requests: ProfileChangeRequest[]) => {
    setShowConfirmDialog({ type: 'confirm', requests });
  };

  const handleRejectGroup = (requests: ProfileChangeRequest[]) => {
    setShowConfirmDialog({ type: 'reject', requests });
  };

  const executeConfirm = async () => {
    if (!showConfirmDialog || !user) return;
    const { type, requests } = showConfirmDialog;
    const ids = requests.map((r) => r.id);

    if (type === 'confirm') {
      setConfirmingId(ids[0]);
    } else {
      setRejectingId(ids[0]);
    }

    try {
      if (type === 'confirm') {
        // 更新 profile_change_requests 狀態
        const { error: reqError } = await supabase
          .from('profile_change_requests')
          .update({
            status: 'confirmed',
            confirmed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .in('id', ids);

        if (reqError) {
          setActionToast({ message: '確認失敗，請稍後再試', type: 'error' });
          return;
        }

        // 更新 creator_profiles 對應欄位
        const updateData: Record<string, string | null> = {
          updated_at: new Date().toISOString(),
        };
        for (const req of requests) {
          updateData[req.field_name] = req.new_value;
        }

        const { error: profileError } = await supabase
          .from('creator_profiles')
          .update(updateData)
          .eq('id', user.id);

        if (profileError) {
          console.error('Error updating profile:', profileError);
          setActionToast({ message: '資料更新失敗', type: 'error' });
          return;
        }

        setActionToast({ message: '已確認變更，資料已更新', type: 'success' });
        loadProfile();
      } else {
        // 拒絕
        const { error } = await supabase
          .from('profile_change_requests')
          .update({
            status: 'rejected',
            updated_at: new Date().toISOString(),
          })
          .in('id', ids);

        if (error) {
          setActionToast({ message: '操作失敗，請稍後再試', type: 'error' });
          return;
        }

        setActionToast({ message: '已拒絕此變更請求', type: 'success' });
      }

      loadPendingRequests();
    } catch (err) {
      console.error('Error:', err);
      setActionToast({ message: '操作失敗', type: 'error' });
    } finally {
      setConfirmingId(null);
      setRejectingId(null);
      setShowConfirmDialog(null);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = '請輸入姓名';
    if (formData.phone && !/^09\d{8}$/.test(formData.phone)) {
      newErrors.phone = '請輸入正確的手機號碼格式（09xxxxxxxx）';
    }
    if (formData.bank_account_number && !formData.bank_name) {
      newErrors.bank_name = '請選擇銀行';
    }
    if (formData.bank_account_number && !formData.bank_account_name) {
      newErrors.bank_account_name = '請輸入戶名';
    }
    if (formData.bank_account_number && !/^\d{10,16}$/.test(formData.bank_account_number)) {
      newErrors.bank_account_number = '帳號格式不正確（10-16位數字）';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !user) return;

    setSaving(true);
    try {
      const updateData: Record<string, string | null> = {
        name: formData.name.trim(),
        line_id: formData.line_id.trim() || null,
        social_links: formData.social_links.trim() || null,
        game_type: formData.game_type.trim() || null,
        updated_at: new Date().toISOString(),
      };

      if (!lockedFields.phone) {
        updateData.phone = formData.phone.trim() || null;
      }

      if (!lockedFields.bank) {
        updateData.bank_name = formData.bank_name || null;
        updateData.bank_branch = formData.bank_branch.trim() || null;
        updateData.bank_account_name = formData.bank_account_name.trim() || null;
        updateData.bank_account_number = formData.bank_account_number.trim() || null;
      }

      const { error } = await supabase
        .from('creator_profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) {
        console.error('Error saving profile:', error);
        setErrors({ submit: '儲存失敗，請稍後再試' });
        return;
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate('/creator/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error:', err);
      setErrors({ submit: '儲存失敗，請稍後再試' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const maskAccount = (account: string) => {
    if (account.length <= 4) return account;
    return '****' + account.slice(-4);
  };

  const getChangeTypeLabel = (type: string) => {
    if (type === 'phone') return '手機號碼';
    if (type === 'bank') return '銀行帳戶';
    return type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-sm">載入中...</p>
        </div>
      </div>
    );
  }

  const groups = groupedRequests();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Toast */}
      {actionToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[70] animate-[fadeIn_0.3s_ease]">
          <div className={`px-6 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2 ${
            actionToast.type === 'success' ? 'bg-teal-500 text-white' : 'bg-red-500 text-white'
          }`}>
            <i className={`${actionToast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-lg`}></i>
            {actionToast.message}
          </div>
        </div>
      )}

      {/* 手機版 Header */}
      <header className="md:hidden bg-slate-900/80 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center">
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-800 transition-colors cursor-pointer mr-3"
          >
            <i className="ri-arrow-left-line text-xl text-white"></i>
          </button>
          <h1 className="text-white font-bold text-base flex-1 text-center mr-9">編輯個人資料</h1>
        </div>
      </header>

      {/* 電腦版 Header */}
      <header className="hidden md:block bg-slate-900/50 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center">
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="flex items-center text-slate-400 hover:text-white transition-colors cursor-pointer mr-4"
          >
            <i className="ri-arrow-left-line text-xl mr-2"></i>
            返回
          </button>
          <h1 className="text-white font-bold text-xl">編輯個人資料</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10 pb-32 md:pb-10">

        {/* 待確認的變更請求通知 */}
        {groups.length > 0 && (
          <section className="mb-6">
            <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-xl overflow-hidden">
              <div className="px-4 md:px-6 py-3 md:py-4 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-full animate-pulse">
                  <i className="ri-notification-3-line text-lg text-amber-400"></i>
                </div>
                <div>
                  <h2 className="text-amber-300 font-bold text-sm md:text-base">管理員申請修改您的資料</h2>
                  <p className="text-slate-400 text-xs">請確認以下變更是否正確，確認後才會正式生效</p>
                </div>
              </div>

              <div className="p-4 md:p-6 space-y-4">
                {groups.map((group) => (
                  <div key={group.key} className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <i className={`${group.type === 'phone' ? 'ri-phone-line' : 'ri-bank-card-line'} text-amber-400`}></i>
                        <span className="text-white text-sm font-medium">{getChangeTypeLabel(group.type)}</span>
                      </div>
                      <span className="text-slate-500 text-xs">
                        {new Date(group.createdAt).toLocaleString('zh-TW')}
                      </span>
                    </div>

                    <div className="p-4 space-y-2.5">
                      {group.requests.map((req) => (
                        <div key={req.id} className="flex items-center gap-3 text-sm">
                          <span className="text-slate-400 text-xs w-20 flex-shrink-0">{FIELD_LABELS[req.field_name] || req.field_name}</span>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-slate-500 line-through text-xs truncate">
                              {req.field_name === 'bank_account_number' && req.old_value
                                ? `****${req.old_value.slice(-4)}`
                                : req.old_value || '空'}
                            </span>
                            <i className="ri-arrow-right-line text-amber-400 text-xs flex-shrink-0"></i>
                            <span className="text-amber-300 text-xs font-medium truncate">
                              {req.field_name === 'bank_account_number' && req.new_value
                                ? `****${req.new_value.slice(-4)}`
                                : req.new_value}
                            </span>
                          </div>
                        </div>
                      ))}

                      {group.adminNote && (
                        <div className="mt-2 bg-slate-900/50 rounded-lg p-3 flex items-start gap-2">
                          <i className="ri-message-3-line text-slate-500 text-xs mt-0.5 flex-shrink-0"></i>
                          <p className="text-slate-400 text-xs leading-relaxed">管理員備註：{group.adminNote}</p>
                        </div>
                      )}
                    </div>

                    <div className="px-4 py-3 border-t border-slate-700/50 flex items-center gap-3">
                      <button
                        onClick={() => handleConfirmGroup(group.requests)}
                        disabled={!!confirmingId || !!rejectingId}
                        className="flex-1 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                      >
                        <i className="ri-check-line"></i>
                        確認無誤
                      </button>
                      <button
                        onClick={() => handleRejectGroup(group.requests)}
                        disabled={!!confirmingId || !!rejectingId}
                        className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                      >
                        <i className="ri-close-line"></i>
                        資料有誤
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 基本資料區塊 */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-500/20 rounded-lg">
              <i className="ri-user-line text-lg text-teal-400"></i>
            </div>
            <h2 className="text-white font-bold text-base">基本資料</h2>
          </div>

          <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4 md:p-6 space-y-4">
            {/* 姓名 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                姓名 / 暱稱 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="請輸入姓名或暱稱"
                className={`w-full bg-slate-900/50 border ${errors.name ? 'border-red-500' : 'border-slate-600'} rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors`}
              />
              {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* 電子郵件（唯讀） */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                電子郵件
              </label>
              <div className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-3 text-slate-400 text-sm flex items-center">
                <i className="ri-lock-line text-slate-500 mr-2"></i>
                {formData.email}
              </div>
              <p className="text-slate-500 text-xs mt-1">電子郵件無法修改</p>
            </div>

            {/* 電話 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                手機號碼
                {lockedFields.phone && (
                  <span className="ml-2 text-amber-400 text-xs font-normal">
                    <i className="ri-lock-line mr-0.5"></i>已鎖定
                  </span>
                )}
              </label>
              {lockedFields.phone ? (
                <>
                  <div className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-3 text-slate-400 text-sm flex items-center">
                    <i className="ri-lock-line text-slate-500 mr-2"></i>
                    {formData.phone}
                  </div>
                  <p className="text-amber-400/80 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-information-line"></i>
                    手機號碼僅能設定一次，如需更改請聯繫管理員
                  </p>
                </>
              ) : (
                <>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="09xxxxxxxx"
                    maxLength={10}
                    className={`w-full bg-slate-900/50 border ${errors.phone ? 'border-red-500' : 'border-slate-600'} rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors`}
                  />
                  {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                  <p className="text-amber-400/60 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    手機號碼設定後將無法自行更改，請確認無誤再儲存
                  </p>
                </>
              )}
            </div>

            {/* LINE ID */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                LINE ID
              </label>
              <input
                type="text"
                value={formData.line_id}
                onChange={(e) => handleChange('line_id', e.target.value)}
                placeholder="請輸入 LINE ID"
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
              />
            </div>

            {/* 社群連結 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                社群媒體連結
              </label>
              <input
                type="text"
                value={formData.social_links}
                onChange={(e) => handleChange('social_links', e.target.value)}
                placeholder="Instagram / TikTok / YouTube 連結"
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
              />
            </div>

            {/* 遊戲類型 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                擅長的遊戲類型
              </label>
              <input
                type="text"
                value={formData.game_type}
                onChange={(e) => handleChange('game_type', e.target.value)}
                placeholder="例如：MOBA、FPS、RPG"
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
              />
            </div>
          </div>
        </section>

        {/* 銀行帳戶區塊 */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-lg">
              <i className="ri-bank-card-line text-lg text-amber-400"></i>
            </div>
            <h2 className="text-white font-bold text-base">銀行帳戶</h2>
            {lockedFields.bank && (
              <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full border border-amber-500/30 flex items-center gap-1">
                <i className="ri-lock-line text-xs"></i>已鎖定
              </span>
            )}
          </div>
          <p className="text-slate-400 text-xs mb-4 ml-10">用於發放獎金，請確保資料正確</p>

          <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-4 md:p-6 space-y-4">
            {lockedFields.bank ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-lock-line text-amber-400"></i>
                </div>
                <div>
                  <p className="text-amber-300 text-xs font-medium mb-0.5">銀行帳戶已鎖定</p>
                  <p className="text-slate-400 text-xs leading-relaxed">銀行帳戶資料僅能設定一次，設定後無法自行更改。如資料有誤，請聯繫管理員協助修改。</p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-amber-400"></i>
                </div>
                <div>
                  <p className="text-amber-300 text-xs font-medium mb-0.5">請注意：銀行帳戶僅能設定一次</p>
                  <p className="text-slate-400 text-xs leading-relaxed">設定後將無法自行更改，如資料有誤需聯繫管理員。請仔細確認所有資料正確無誤後再儲存。</p>
                </div>
              </div>
            )}

            {/* 銀行名稱 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                銀行名稱 {!lockedFields.bank && formData.bank_account_number && <span className="text-red-400">*</span>}
              </label>
              {lockedFields.bank ? (
                <div className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-3 text-slate-400 text-sm flex items-center">
                  <i className="ri-lock-line text-slate-500 mr-2"></i>
                  {formData.bank_name || '未設定'}
                </div>
              ) : (
                <>
                  <select
                    value={formData.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    className={`w-full bg-slate-900/50 border ${errors.bank_name ? 'border-red-500' : 'border-slate-600'} rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-teal-500 transition-colors appearance-none cursor-pointer`}
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                  >
                    <option value="">請選擇銀行</option>
                    {BANK_LIST.map((bank) => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                  {errors.bank_name && <p className="text-red-400 text-xs mt-1">{errors.bank_name}</p>}
                </>
              )}
            </div>

            {/* 分行名稱 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                分行名稱
              </label>
              {lockedFields.bank ? (
                <div className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-3 text-slate-400 text-sm flex items-center">
                  <i className="ri-lock-line text-slate-500 mr-2"></i>
                  {formData.bank_branch || '未設定'}
                </div>
              ) : (
                <input
                  type="text"
                  value={formData.bank_branch}
                  onChange={(e) => handleChange('bank_branch', e.target.value)}
                  placeholder="例如：忠孝分行"
                  className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                />
              )}
            </div>

            {/* 戶名 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                戶名 {!lockedFields.bank && formData.bank_account_number && <span className="text-red-400">*</span>}
              </label>
              {lockedFields.bank ? (
                <div className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-3 text-slate-400 text-sm flex items-center">
                  <i className="ri-lock-line text-slate-500 mr-2"></i>
                  {formData.bank_account_name || '未設定'}
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={formData.bank_account_name}
                    onChange={(e) => handleChange('bank_account_name', e.target.value)}
                    placeholder="請輸入帳戶戶名"
                    className={`w-full bg-slate-900/50 border ${errors.bank_account_name ? 'border-red-500' : 'border-slate-600'} rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors`}
                  />
                  {errors.bank_account_name && <p className="text-red-400 text-xs mt-1">{errors.bank_account_name}</p>}
                </>
              )}
            </div>

            {/* 帳號 */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">
                銀行帳號
              </label>
              {lockedFields.bank ? (
                <div className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-3 text-slate-400 text-sm flex items-center">
                  <i className="ri-lock-line text-slate-500 mr-2"></i>
                  <span className="tracking-wider">****{formData.bank_account_number.slice(-4)}</span>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={formData.bank_account_number}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      handleChange('bank_account_number', val);
                    }}
                    placeholder="請輸入銀行帳號（10-16位數字）"
                    maxLength={16}
                    inputMode="numeric"
                    className={`w-full bg-slate-900/50 border ${errors.bank_account_number ? 'border-red-500' : 'border-slate-600'} rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors tracking-wider`}
                  />
                  {errors.bank_account_number && <p className="text-red-400 text-xs mt-1">{errors.bank_account_number}</p>}
                  {formData.bank_account_number && !errors.bank_account_number && (
                    <p className="text-slate-500 text-xs mt-1">
                      顯示為：{maskAccount(formData.bank_account_number)}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* 錯誤提示 */}
        {errors.submit && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 mb-6 flex items-center gap-3">
            <i className="ri-error-warning-line text-xl text-red-400"></i>
            <p className="text-red-300 text-sm">{errors.submit}</p>
          </div>
        )}

        {/* 儲存按鈕 - 電腦版 */}
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                儲存中...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-2"></i>
                儲存變更
              </>
            )}
          </button>
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
        </div>
      </div>

      {/* 手機版底部儲存按鈕 */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-lg border-t border-slate-700 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] z-50">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white rounded-xl font-bold text-base transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              儲存中...
            </>
          ) : (
            <>
              <i className="ri-save-line mr-2 text-lg"></i>
              儲存變更
            </>
          )}
        </button>
      </div>

      {/* 確認/拒絕彈窗 */}
      {showConfirmDialog && (
        <div
          className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-6"
          onClick={() => setShowConfirmDialog(null)}
        >
          <div
            className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className={`w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full ${
                showConfirmDialog.type === 'confirm' ? 'bg-teal-500/20' : 'bg-red-500/20'
              }`}>
                <i className={`text-3xl ${
                  showConfirmDialog.type === 'confirm' ? 'ri-check-double-line text-teal-400' : 'ri-close-circle-line text-red-400'
                }`}></i>
              </div>
              <h3 className="text-white text-lg font-bold mb-2">
                {showConfirmDialog.type === 'confirm' ? '確認變更資料？' : '拒絕此變更？'}
              </h3>
              <p className="text-slate-400 text-sm">
                {showConfirmDialog.type === 'confirm'
                  ? '確認後資料將立即更新，此操作無法撤銷'
                  : '拒絕後管理員會收到通知，可能會再次聯繫您確認'}
              </p>

              {/* 顯示變更內容摘要 */}
              <div className="mt-4 bg-slate-900/50 rounded-lg p-3 text-left">
                {showConfirmDialog.requests.map((req) => (
                  <div key={req.id} className="flex items-center gap-2 text-xs py-1">
                    <span className="text-slate-500 w-16 flex-shrink-0">{FIELD_LABELS[req.field_name] || req.field_name}</span>
                    <span className="text-slate-500 line-through truncate">
                      {req.field_name === 'bank_account_number' && req.old_value
                        ? `****${req.old_value.slice(-4)}`
                        : req.old_value || '空'}
                    </span>
                    <i className="ri-arrow-right-line text-amber-400 flex-shrink-0"></i>
                    <span className="text-amber-300 font-medium truncate">
                      {req.field_name === 'bank_account_number' && req.new_value
                        ? `****${req.new_value.slice(-4)}`
                        : req.new_value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmDialog(null)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={executeConfirm}
                disabled={!!confirmingId || !!rejectingId}
                className={`flex-1 py-3 rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                  showConfirmDialog.type === 'confirm'
                    ? 'bg-teal-500 hover:bg-teal-600 text-white'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                {(confirmingId || rejectingId) ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    處理中...
                  </span>
                ) : showConfirmDialog.type === 'confirm' ? '確認變更' : '確定拒絕'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 成功彈窗 */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-sm border border-slate-700 text-center animate-[fadeInScale_0.3s_ease]">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-teal-500/20 rounded-full">
              <i className="ri-check-line text-4xl text-teal-400"></i>
            </div>
            <h3 className="text-white text-lg font-bold mb-2">儲存成功！</h3>
            <p className="text-slate-400 text-sm">個人資料已更新</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, -10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
